import React, { useEffect, useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
import { Lightbulb, Split, Shuffle, Puzzle, ListOrdered, Sparkles, Calculator, CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import { QuestionRendererProps } from './types';
import { Input } from '../../../components/ui/Input';
import { StrategySolverConfig, StrategySolverStrategy } from '../../../content/types';
import { NUMERIC_TOLERANCE } from '../../../content/questions/validators/shared';
/**
 * StrategySolverRenderer — "Reken handig" question type.
 *
 * Two-phase flow:
 *  1. Student picks a strategy from 3-5 cards (splitsen, compenseren,
 *     factoren, verdubbel-halveer, cijferend, ...).
 *  2. The chosen strategy's hint surfaces and the answer input unlocks.
 *
 * The user payload is JSON: { strategyId, answer }. Validation is binary
 * on the answer; the strategy tier is consumed in feedback to acknowledge
 * the choice ("Slim gekozen — splitsen werkt hier prima").
 */
const ICON_MAP: Record<string, ComponentType<{
  className?: string;
}>> = {
  split: Split,
  swap: Shuffle,
  shuffle: Shuffle,
  puzzle: Puzzle,
  list: ListOrdered,
  sparkles: Sparkles,
  calculator: Calculator,
  lightbulb: Lightbulb
};
function resolveIcon(name?: string) {
  if (!name) return Lightbulb;
  return ICON_MAP[name.toLowerCase()] || Lightbulb;
}
function normalizeNumeric(input: string): string {
  return String(input).replace(/[€\s]/g, '').replace(',', '.').trim();
}
function isAnswerAccepted(userVal: string, accepted: (string | number)[]): boolean {
  const trimmed = userVal.trim();
  if (!trimmed) return false;
  return accepted.some(ans => {
    const acceptedStr = String(ans).trim();
    if (acceptedStr.toLowerCase() === trimmed.toLowerCase()) return true;
    const userNum = parseFloat(normalizeNumeric(trimmed));
    const acceptedNum = parseFloat(normalizeNumeric(acceptedStr));
    if (!isNaN(userNum) && !isNaN(acceptedNum)) {
      return Math.abs(userNum - acceptedNum) < NUMERIC_TOLERANCE;
    }
    return false;
  });
}
export function StrategySolverRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const config = (question.data?.strategySolver || {}) as Partial<StrategySolverConfig>;
  const strategies: StrategySolverStrategy[] = config.strategies || [];
  const expression = config.expression || '';
  const unit = config.unit || '';
  const acceptedAnswers: (string | number)[] = config.acceptedAnswers ?? (question.correctAnswer !== undefined ? [question.correctAnswer] : []);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(null);
  const [answer, setAnswer] = useState('');
  // Reset when question changes
  useEffect(() => {
    setSelectedStrategyId(null);
    setAnswer('');
  }, [question.id]);
  // Ready when both phases are completed
  useEffect(() => {
    const ready = !!selectedStrategyId && answer.trim().length > 0;
    onReadyChange?.(ready);
  }, [selectedStrategyId, answer, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify({
    strategyId: selectedStrategyId,
    answer: answer.trim()
  }), [selectedStrategyId, answer]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const selectedStrategy = strategies.find(s => s.id === selectedStrategyId);
  const isCorrect = isAnswerAccepted(answer, acceptedAnswers);
  const color = moduleColor || '#6366f1';
  return <div className="w-full max-w-2xl mx-auto flex flex-col gap-6" data-id="element-11864">
      {/* Expression focus card */}
      {expression && <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} className="rounded-2xl border-2 px-6 py-8 flex items-center justify-center text-center" style={{
      borderColor: `${color}40`,
      backgroundColor: `${color}0a`
    }} data-id="element-11865">
          <span className="text-4xl font-bold tabular-nums tracking-tight text-slate-900" data-id="element-11866">
            {expression}
          </span>
        </motion.div>}

      {/* Phase 1 — Strategy picker */}
      <div className="flex flex-col gap-3" data-id="element-11867">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700" data-id="element-11868">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold" style={{
          backgroundColor: color
        }} data-id="element-11869">
            1
          </span>
          Welke handige manier kies je?
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-11870">
          {strategies.map(strategy => {
          const Icon = resolveIcon(strategy.icon);
          const isSelected = strategy.id === selectedStrategyId;
          const locked = shouldValidate;
          return <motion.button key={strategy.id} type="button" disabled={locked} onClick={() => {
            if (!locked) setSelectedStrategyId(strategy.id);
          }} whileHover={!locked ? {
            scale: 1.02
          } : undefined} whileTap={!locked ? {
            scale: 0.98
          } : undefined} className={`relative text-left p-4 rounded-xl border-2 transition-colors ${isSelected ? 'shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'} ${locked ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'}`} style={isSelected ? {
            borderColor: color,
            backgroundColor: `${color}0d`
          } : undefined} aria-pressed={isSelected} data-id="element-11871">
                <div className="flex items-start gap-3" data-id="element-11872">
                  <div className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{
                backgroundColor: isSelected ? color : `${color}1a`,
                color: isSelected ? '#fff' : color
              }} data-id="element-11873">
                    <Icon className="w-5 h-5" data-id="element-11874" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-11875">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-11876">
                      {strategy.label}
                    </div>
                  </div>
                  {isSelected && <CheckCircle2 className="w-5 h-5 shrink-0" style={{
                color
              }} data-id="element-11877" />}
                </div>
              </motion.button>;
        })}
        </div>
      </div>

      {/* Phase 2 — Answer input (unlocks after strategy pick) */}
      <AnimatePresence data-id="element-11878">
        {selectedStrategyId && <motion.div key="answer-phase" initial={{
        opacity: 0,
        y: 8,
        height: 0
      }} animate={{
        opacity: 1,
        y: 0,
        height: 'auto'
      }} exit={{
        opacity: 0,
        y: 8,
        height: 0
      }} transition={{
        duration: 0.2
      }} className="flex flex-col gap-3 overflow-hidden" data-id="element-11879">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700" data-id="element-11880">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold" style={{
            backgroundColor: color
          }} data-id="element-11881">
                2
              </span>
              Reken nu uit{' '}
              {selectedStrategy ? `met ${selectedStrategy.label.toLowerCase()}` : ''}
              :
            </div>

            {selectedStrategy?.hint && <div className="flex items-start gap-2 px-3 py-2 rounded-lg text-xs" style={{
          backgroundColor: `${color}10`,
          color: '#334155'
        }} data-id="element-11882">
                <Lightbulb className="w-4 h-4 shrink-0 mt-0.5" style={{
            color
          }} data-id="element-11883" />
                <span data-id="element-11884">{selectedStrategy.hint}</span>
              </div>}

            <div className="flex items-center gap-2" data-id="element-11885">
              {unit && <span className="text-2xl font-semibold text-slate-700" data-id="element-11886">
                  {unit}
                </span>}
              <Input value={answer} onChange={e => {
            if (shouldValidate) return;
            setAnswer(e.target.value);
          }} placeholder="Typ je antwoord..." inputMode="decimal" disabled={shouldValidate} aria-label="Antwoord" className="text-2xl font-bold text-center tabular-nums" data-id="element-11887" />
              {shouldValidate && (isCorrect ? <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0" data-id="element-11888" /> : <XCircle className="w-6 h-6 text-red-500 shrink-0" data-id="element-11889" />)}
            </div>

            {/* Validation hint after submission */}
            {shouldValidate && selectedStrategy && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="text-xs text-slate-600 flex items-start gap-1.5 pl-1" data-id="element-11890">
                <ArrowRight className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" data-id="element-11891" />
                <span data-id="element-11892">
                  Gekozen strategie: <strong data-id="element-11893">{selectedStrategy.label}</strong>
                  {selectedStrategy.tier === 'suboptimal' && <> — werkt, maar er was een handigere weg.</>}
                  {selectedStrategy.tier === 'optimal' && <> — slim gekozen!</>}
                </span>
              </motion.div>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
StrategySolverRenderer.displayName = 'StrategySolverRenderer';